import { Direction } from '../types/Direction';
import { move } from './move';

interface MoveInDirectionReturn<T> {
    /** Reordered items array  */
    movedItems: Array<T>;
    /** New index of the reordered item */
    newPosition: number;
}

/**
 * Move an item from an array in a given direction.
 *
 * @param  array      A list of items.
 * @param  index      The index to move
 * @param  direction  The direction in which to move it
 * @param  shouldIgnoreNode  Callback to optionally ignore nodes and move to the next.
 * @return Object containing the reordered array and the item's new position.
 */
export const moveInDirection = <T>(
    array: Array<T>,
    nodePosition: number,
    direction: Direction,
    shouldIgnoreNode?: (nextNode: T, nextNodePosition: number) => boolean,
): MoveInDirectionReturn<T> => {
    const nodeNextPosition = nodePosition + (direction === Direction.UP ? -1 : +1);

    /**
     * If the given node position or next position is outside of array,
     * return same data as given.
     */
    if (
        nodePosition < 0 ||
        nodePosition >= array.length ||
        nodeNextPosition === -1 ||
        nodeNextPosition >= array.length
    ) {
        return {
            movedItems: array,
            newPosition: nodePosition,
        };
    }

    // Move the node to the next position
    const movedNodes = move(array, nodePosition, nodeNextPosition);

    // If the next position has been marked as to be ignored, recursively continue to the next.
    if (shouldIgnoreNode && shouldIgnoreNode(array[nodeNextPosition], nodeNextPosition)) {
        return moveInDirection(movedNodes, nodeNextPosition, direction, shouldIgnoreNode);
    }

    return {
        movedItems: movedNodes,
        newPosition: nodeNextPosition,
    };
};
